/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  AccountSummaryCompareField,
  AccountSummaryLinkageRow,
} from "../../api/accounts";
import { ValueOrEmpty } from "../record-review/LedgerRow";
import {
  buildSourceRecordModel,
  isIdentifierLabel,
  searchTypeLabel,
  SourceFieldEntry,
  SourceFieldGroup,
} from "./helpers/sourceFields";
import classes from "./css/accountSummary.module.css";

/*
 * A missing value is a fact worth stating: the comparison-table rule renders
 * the literal word in italic grey rather than leaving a blank cell, because a
 * blank and an empty value are different things. The word itself is the review
 * surface's - one spelling of "empty" across both cards - and only the
 * sentence explaining it is local, because here the blank belongs to an
 * enrichment source rather than to the CRM.
 */
const SOURCE_EMPTY_TOOLTIP = "This source holds no value for this field.";

const SourceEmptyValue = () => (
  <ValueOrEmpty value={null} emptyTooltip={SOURCE_EMPTY_TOOLTIP} />
);

/*
 * A leading icon per headline metric, matched on the display label. Ordered
 * so the more specific concept wins ("Total Article Reads" is a readership
 * number before it is an article). Decoration in the info tone, not status -
 * the one-ramp rule stays intact.
 */
const FIELD_ICONS: [RegExp, typeof ArticleOutlinedIcon][] = [
  [/read|view/i, VisibilityOutlinedIcon],
  [/article|publication|title/i, ArticleOutlinedIcon],
  [/contributor|author|person/i, PersonOutlinedIcon],
  [/industry|sector/i, DomainOutlinedIcon],
  [/tag|topic/i, SellOutlinedIcon],
  [/company|firm/i, BusinessOutlinedIcon],
];

const fieldIconFor = (label: string) => {
  const match = FIELD_ICONS.find(([pattern]) => pattern.test(label));
  return match ? match[1] : LabelOutlinedIcon;
};

/*
 * The insight a steward acts on, in words rather than a status colour (the
 * one-ramp rule): a source value the CRM lacks entirely is an addition, not a
 * disagreement, so the two cases are named differently. Identifier fields
 * never get the disagreement voice - see isIdentifierLabel.
 */
const DeltaAnnotation = (props: {
  compare?: AccountSummaryCompareField;
  identifier?: boolean;
  /*
   * The CRM named, not abbreviated: "Differs from CRM" made the steward hold
   * a mapping in their head, and this panel already names the source on the
   * other side of the comparison. Falls back to the generic word when the
   * vendor is unrecognised.
   */
  crmLabel: string;
}) => {
  if (!props.compare || props.compare.is_matching !== false) {
    return null;
  }
  if (props.identifier) {
    /* A source-side id the CRM doesn't hold needs no annotation at all. */
    return props.compare.crm_value ? (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {`${props.crmLabel}: ${props.compare.crm_value}`}
      </Typography>
    ) : null;
  }
  return (
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {props.compare.crm_value ? (
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {`Differs from ${props.crmLabel}`}
          </Box>
          {` · ${props.crmLabel}: ${props.compare.crm_value}`}
        </>
      ) : (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {`Not in ${props.crmLabel}`}
        </Box>
      )}
    </Typography>
  );
};

/*
 * The mapped mark: this field is one the enrich project consumes. A mark
 * rather than a word because it recurs on every mapped row, and the delta
 * annotation beneath already says what the mapping found. Info tone, like
 * every other decorative icon on the card.
 */
const MappedMark = () => (
  <Tooltip title="Mapped in the enrich project">
    <LinkOutlinedIcon
      className={classes.mappedMark}
      sx={{ color: "info.main" }}
      aria-label="Mapped in the enrich project"
    />
  </Tooltip>
);

/*
 * Headline numbers as borderless tiles, like the mock's "321 Total Offices"
 * column: the metrics the project chose to map are the source's headline.
 */
const StatTile = (props: { entry: SourceFieldEntry; crmLabel: string }) => {
  const Icon = fieldIconFor(props.entry.label);
  return (
    <div className={classes.statTile}>
      <Icon className={classes.statTileIcon} sx={{ color: "info.main" }} />
      <div className={classes.statTileText}>
        <Typography
          variant="h4"
          component="p"
          className={classes.tabularNumber}
        >
          {props.entry.display}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {props.entry.label}
        </Typography>
        <DeltaAnnotation
          compare={props.entry.compare}
          identifier={isIdentifierLabel(props.entry.label)}
          crmLabel={props.crmLabel}
        />
      </div>
    </div>
  );
};

/* Lists beyond this many items fold their tail into one "+N" chip. */
const CHIP_LIMIT = 8;

/*
 * A comma-joined list rendered as the chips the comparison surfaces already
 * use for field values. The lead item is filled when the source named it on
 * its own - "Top Tag" beside "Top Tags" - so the emphasis the source gave it
 * survives the fold.
 */
const ChipList = (props: { entry: SourceFieldEntry }) => {
  const items = props.entry.items ?? [];
  const shown = items.slice(0, CHIP_LIMIT);
  const hidden = items.slice(CHIP_LIMIT);
  return (
    <div className={classes.chipList}>
      {shown.map((item, index) => (
        <Chip
          key={`${item}-${index}`}
          size="small"
          variant={props.entry.leadItem && index === 0 ? "filled" : "outlined"}
          label={item}
          title={item}
          className={classes.valueChip}
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip title={hidden.join(", ")}>
          <Chip
            size="small"
            variant="outlined"
            label={`+${hidden.length}`}
            className={classes.valueChip}
            sx={{ color: "text.secondary" }}
          />
        </Tooltip>
      )}
    </div>
  );
};

/* The value alone, in the shape its kind asks for. */
const SourceValue = (props: { entry: SourceFieldEntry }) => {
  const { entry } = props;
  if (entry.kind === "empty") {
    return <SourceEmptyValue />;
  }
  if (entry.kind === "list") {
    return <ChipList entry={entry} />;
  }
  if (entry.href) {
    return (
      <Link
        variant="body2"
        href={entry.href}
        target="_blank"
        rel="noopener noreferrer"
        color="text.primary"
        underline="always"
        className={classes.fieldValue}
      >
        {entry.display}
      </Link>
    );
  }
  if (entry.kind === "date" && entry.relative) {
    /* The date is the fact; the words are how far away it is. */
    return (
      <Typography variant="body2" className={classes.fieldValue}>
        {entry.display}
        <Box
          component="span"
          className={classes.relativeDate}
          sx={{ color: "text.secondary" }}
        >
          {` · ${entry.relative}`}
        </Box>
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      className={
        entry.kind === "metric"
          ? `${classes.fieldValue} ${classes.tabularNumber}`
          : classes.fieldValue
      }
    >
      {entry.display}
    </Typography>
  );
};

/* Values that want the row's full width rather than half a sheet. */
const isWide = (entry: SourceFieldEntry) =>
  entry.kind === "list" ||
  entry.kind === "url" ||
  Boolean(entry.href) ||
  (entry.display ?? "").length > 40;

/* Label left, value right - the full record's row grammar. */
const FieldRow = (props: { entry: SourceFieldEntry; crmLabel: string }) => {
  const { entry } = props;
  return (
    <div
      className={
        isWide(entry)
          ? `${classes.sourceRow} ${classes.sourceWide}`
          : classes.sourceRow
      }
    >
      <div className={classes.sourceRowLabel}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {entry.label}
        </Typography>
        {entry.mapped && <MappedMark />}
      </div>
      <div className={classes.fieldValueStack}>
        <SourceValue entry={entry} />
        <DeltaAnnotation
          compare={entry.compare}
          identifier={isIdentifierLabel(entry.label)}
          crmLabel={props.crmLabel}
        />
      </div>
    </div>
  );
};

/*
 * Unmapped fields the source holds nothing for, as one line. The word is the
 * review surface's "empty", so a steward reading both cards meets one
 * vocabulary; the names say what the source could carry, which is the
 * answer to "what else could we map?".
 */
const EmptyLine = (props: { entries: SourceFieldEntry[] }) => (
  <Typography
    variant="body2"
    sx={{ color: "text.secondary", fontStyle: "italic" }}
    className={classes.sourceEmptyLine}
  >
    {`Empty: ${props.entries.map((entry) => entry.label).join(", ")}`}
  </Typography>
);

/*
 * One of the source's groups: a heading when the group has a name (the
 * ungrouped identity fields read straight off the Linked line without one),
 * the rows flowing across the sheet, and the empty inventory as a footer.
 */
const GroupSection = (props: { group: SourceFieldGroup; crmLabel: string }) => {
  const { group } = props;
  return (
    <section
      className={classes.sourceGroup}
      aria-label={group.label ?? "Record"}
    >
      {group.label && (
        <Typography
          variant="body2"
          component="h3"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          {group.label}
        </Typography>
      )}
      {group.fields.length > 0 && (
        <div className={classes.sourceSheet}>
          {group.fields.map((entry) => (
            <FieldRow key={entry.key} entry={entry} crmLabel={props.crmLabel} />
          ))}
        </div>
      )}
      {group.emptyFields.length > 0 && (
        <EmptyLine entries={group.emptyFields} />
      )}
    </section>
  );
};

/**
 * One matched reference record: a single confirmation line in the product's
 * success tone, the project's headline metrics as tiles, then the source's
 * record in the groups the source itself keeps it in (see
 * buildSourceRecordModel). The reference name repeats the account title on a
 * clean match, so it only appears when the source knows the company by a
 * different name.
 */
export const EnrichTabContents = (props: {
  row: AccountSummaryLinkageRow;
  systemName?: string;
  crmCompanyName?: string | null;
  crmLabel: string;
}) => {
  const { row } = props;
  const model = useMemo(
    () => buildSourceRecordModel(row, props.systemName),
    [row, props.systemName]
  );
  const fieldCompare = row.field_compare;
  const sourceUrl = fieldCompare?.reference_record?.source_record_url;
  const detailParts = [
    row.datasource_id,
    searchTypeLabel(row.search_type),
    /*
     * The count on the Linked line is the count actually on screen - tiles,
     * rows and the empty lines - never an estimate of it.
     */
    model.fieldCount > 0
      ? `${model.fieldCount} ${model.fieldCount === 1 ? "field" : "fields"}, ${
          model.mappedCount
        } mapped`
      : null,
  ].filter(Boolean);
  const showName =
    Boolean(row.company_name) &&
    row.company_name!.trim().toLowerCase() !==
      (props.crmCompanyName || "").trim().toLowerCase();

  return (
    <div className={classes.linkedBlock}>
      <div className={classes.linkedLine}>
        <CheckCircleOutlineOutlinedIcon
          className={classes.linkedIcon}
          sx={{ color: "success.main" }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Linked
        </Typography>
        {showName &&
          (sourceUrl ? (
            <Link
              variant="body2"
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="text.primary"
              underline="always"
              sx={{ fontWeight: 600 }}
            >
              {row.company_name}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.company_name}
            </Typography>
          ))}
        {/*
         * The source's verdict, beside the name it belongs to and before
         * the provenance: a company the register says is dissolved is a
         * different account from a live one, and the steward should meet
         * that word before reading a single field. Neutral chrome - status
         * words are the source's, and the ramp is reserved for graded
         * quality, which this is not.
         */}
        {model.status && (
          <Tooltip title={model.status.label}>
            <Chip
              size="small"
              variant="outlined"
              label={model.status.display}
              className={classes.statusChip}
              sx={{ borderColor: "text.secondary", color: "text.primary" }}
            />
          </Tooltip>
        )}
        {detailParts.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {detailParts.join(" · ")}
          </Typography>
        )}
        {/* The name usually carries the source link; without it, say so. */}
        {!showName && sourceUrl && (
          <Link
            variant="caption"
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="text.secondary"
            underline="always"
          >
            View source record
          </Link>
        )}
      </div>

      {model.headline.length > 0 && (
        <div className={classes.statRow}>
          {model.headline.map((entry) => (
            <StatTile key={entry.key} entry={entry} crmLabel={props.crmLabel} />
          ))}
        </div>
      )}

      {model.groups.length > 0 && (
        <div className={classes.sourceGroups}>
          {model.groups.map((group) => (
            <GroupSection
              key={group.key}
              group={group}
              crmLabel={props.crmLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
};
